// client/src/features/properties/costs-search.ts — the URL state of SCR-PROP-003's „Costuri &
// P&L economic" tab · EXP-005 phase 2 (04 §9.2.3).
//
// One route, two readings, one schema. The tab renders FIN-001's per-property P&L above the
// per-property ledger of SCR-EXP-001 (D-713), and both take their parameters from the URL so a
// shared link opens on the same period with the same filters.
//
//   • `basis`, `period`, `year` are `GET /api/v1/properties/{id}/economic-pnl`'s own names.
//     `month` and `quarter` are NOT here: the endpoint defaults both to the current one, and a
//     month picker on a tab whose question is „how is this property doing" is a report
//     screen's control (SCR-REPT-002), not this one's.
//   • The ledger half is `expenseSearchSchema` MINUS `propertyId`, which is the path segment
//     on this route rather than a filter (D-713).
//
// `basis` and `period` are `z.enum` over the generated unions' members so a value the contract
// drops fails `tsc` here; both have defaults, so the URL may omit them.

import { z } from 'zod';

import type { PnlBasis, ReportingPeriodKind } from '@/client';

import { DEFAULT_EXPENSE_SORT, expenseSearchSchema, type ExpenseSearch } from '../expenses/search';

const BASIS_ORDER: Record<PnlBasis, true> = { ACCRUAL: true, CASH: true };
export const PNL_BASES = Object.keys(BASIS_ORDER) as readonly PnlBasis[];

const PERIOD_ORDER: Record<ReportingPeriodKind, true> = {
  YTD: true,
  MONTH: true,
  QUARTER: true,
  YEAR: true,
};
export const REPORTING_PERIODS = Object.keys(PERIOD_ORDER) as readonly ReportingPeriodKind[];

export const propertyCostsSearchSchema = expenseSearchSchema.omit({ propertyId: true }).extend({
  basis: z.enum(PNL_BASES).default('ACCRUAL'),
  period: z.enum(REPORTING_PERIODS).default('YTD'),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});

export type PropertyCostsSearch = z.infer<typeof propertyCostsSearchSchema>;

/** The ledger's view of the tab's URL: its own filters, with the route's property as the scope. */
export function toLedgerSearch(search: PropertyCostsSearch, propertyId: string): ExpenseSearch {
  return {
    page: search.page,
    pageSize: search.pageSize,
    sort: search.sort,
    q: search.q,
    categoryId: search.categoryId,
    status: search.status,
    from: search.from,
    to: search.to,
    isCapex: search.isCapex,
    propertyId,
  };
}

/** A ledger filter change, folded back into the tab's URL — the P&L half is untouched. */
export function withLedgerSearch(
  previous: PropertyCostsSearch,
  next: ExpenseSearch,
): PropertyCostsSearch {
  return {
    ...previous,
    page: next.page,
    pageSize: next.pageSize,
    sort: next.sort,
    q: next.q,
    categoryId: next.categoryId,
    status: next.status,
    from: next.from,
    to: next.to,
    isCapex: next.isCapex,
  };
}

export const CLEARED_PROPERTY_COSTS_SEARCH: PropertyCostsSearch = {
  page: 1,
  pageSize: 25,
  sort: DEFAULT_EXPENSE_SORT,
  basis: 'ACCRUAL',
  period: 'YTD',
};
